import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  withLatestFrom
} from 'rxjs/operators';

import { Go, ShowError, ShowInfo } from '@ui/legacy-lib';
import { RentalDepositFacade } from '../../core/services/rental-deposit.facade';
import { RentDepositStepRoutes } from '../../components/rent-deposit-wizard/rent-deposit-step.routes';
import { notificationConfig } from '../../config/notification.config';
import {
  LoadDepositInfo,
  LoadDepositInfoFail,
  LoadDepositInfoSuccess,
  RentDepositWizardGoToStep,
  RentDepositWizardNextStep,
  RentDepositWizardPreviousStep,
  UseRuvDeposit,
  UseRuvDepositFail,
  UseRuvDepositSuccess
} from './rent-deposit.actions';
import { getCurrentWizardStep } from './rent-deposit.selectors';

@Injectable()
export class RentDepositEffects {
  private actions$ = inject(Actions);
  goToStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentDepositWizardGoToStep),
      map(({ id, stepNumber }) => {
        return new Go({
          path: ['rent-deposit', id, RentDepositStepRoutes[stepNumber].path]
        });
      })
    )
  );
  private store = inject(Store);
  nextStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentDepositWizardNextStep),
      withLatestFrom(this.store.select(getCurrentWizardStep)),
      map(([{ id }, currentStepNumber]) => {
        return new Go({
          path: [
            'rent-deposit',
            id,
            RentDepositStepRoutes[currentStepNumber - 1].path
          ]
        });
      })
    )
  );

  prevStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RentDepositWizardPreviousStep),
      withLatestFrom(this.store.select(getCurrentWizardStep)),
      map(([{ id }, currentStepNumber]) => {
        return new Go({
          path: [
            'rent-deposit',
            id,
            RentDepositStepRoutes[currentStepNumber - 1].path
          ]
        });
      })
    )
  );
  private facade = inject(RentalDepositFacade);
  useRuvDeposit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UseRuvDeposit),
      switchMap(({ propertyId, input }) =>
        this.facade.useRuvDeposit(propertyId, input).pipe(
          mergeMap(rentDepositInfo => [
            UseRuvDepositSuccess({ rentDepositInfo }),
            new ShowInfo(notificationConfig.rentDeposit.useRuvDeposit.success)
          ]),
          catchError(error => [
            UseRuvDepositFail(error),
            new ShowError(notificationConfig.rentDeposit.useRuvDeposit.error)
          ])
        )
      )
    )
  );

  loadDepositOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadDepositInfo),
      switchMap(({ propertyId }) =>
        this.facade.loadDepositInfo(propertyId).pipe(
          map(depositInfo => LoadDepositInfoSuccess({ depositInfo })),
          catchError(error => [
            LoadDepositInfoFail(error),
            new ShowError(
              notificationConfig.rentDeposit.loadDepositOptions.error
            )
          ])
        )
      )
    )
  );
}
